import React, { useState } from 'react';
import SEO from '../components/common/SEO';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { products, productCategories } from '../data/products';

const Products: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('All');

  const filteredProducts = activeCategory === 'All'
    ? products
    : products.filter(p => p.category === activeCategory);

  return (
    <>
      <SEO
        title="Premium Tissue Products - Complete Catalog"
        description="Explore Skytion Enterprises' complete range of premium tissue products. From toilet rolls to kitchen towels, facial tissues to commercial dispensers."
        keywords="tissue products, paper cups, food containers, kraft bowls, wooden cutlery, eco packaging, skytion enterprises"
      />

      {/* Hero Section */}
      <section
        className="py-5"
        style={{
          backgroundImage: 'url(/assets/skytion/backgrounds/tissue paper showcase image.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backgroundBlendMode: 'overlay'
        }}
      >
        <Container>
          <Row className="text-center py-5">
            <Col lg={8} className="mx-auto">
              <h1 className="display-4 mb-4">Complete Skytion Product Catalog</h1>
              <p className="lead mb-4">
                Discover our premium range of tissue products. From signature toilet rolls to eco-friendly kitchen towels, facial tissues, and commercial dispensers.
              </p>
              <div className="d-flex justify-content-center gap-4">
                <div className="text-center">
                  <h3 className="text-primary mb-0">{products.length}</h3>
                  <small className="text-muted">Product Lines</small>
                </div>
                <div className="text-center">
                  <h3 className="text-success mb-0">{productCategories.length}</h3>
                  <small className="text-muted">Categories</small>
                </div>
                <div className="text-center">
                  <h3 className="text-info mb-0">2</h3>
                  <small className="text-muted">Brands</small>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Product Grid */}
      <section className="py-24 bg-slate-50 border-t border-slate-200">
        <Container>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-semibold tracking-tight text-slate-900 mb-4">Complete Product Catalog</h2>
            <p className="text-lg text-slate-600">Browse our full range of premium tissues and eco-friendly packaging</p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            <button
              onClick={() => setActiveCategory('All')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === 'All'
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                }`}
            >
              All Products
            </button>
            {productCategories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === cat.id
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50 hover:text-slate-900'
                  }`}
              >
                {cat.name}
              </button>
            ))}
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <div key={product.id} className="group relative flex flex-col bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-xl hover:border-slate-300 transition-all duration-300 overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 overflow-hidden relative">
                  <img
                    src={product.images[0]?.src || ''}
                    alt={product.images[0]?.alt || product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="inline-flex items-center rounded-md bg-white/90 backdrop-blur-sm px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm border border-slate-200">
                      {product.brand}
                    </span>
                    <span className="inline-flex items-center rounded-md bg-white/90 backdrop-blur-sm px-2.5 py-1 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                      {product.specs.ply} Ply
                    </span>
                  </div>
                </div>
                <div className="flex flex-col flex-grow p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{product.name}</h3>
                  <p className="text-sm text-slate-600 mb-6 flex-grow leading-relaxed line-clamp-3">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                      {product.specs.material}
                    </span>
                    {product.specs.mrp && (
                      <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700">
                        MRP {product.specs.mrp}
                      </span>
                    )}
                  </div>
                  <Link
                    to={`/products/${product.slug}`}
                    onClick={() => window.scrollTo({ top: 0 })}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 border border-slate-200 transition-colors hover:bg-slate-900 hover:text-white"
                    style={{ textDecoration: 'none' }}
                  >
                    View Specifications
                  </Link>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-slate-500">No products found in this category.</p>
            </div>
          )}
        </Container>
      </section>

      {/* Contact for Custom Orders */}
      <section
        className="py-5 custom-orders-section"
        style={{
          backgroundImage: 'url(/assets/skytion/backgrounds/tissue paper wallpaper 0023.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backgroundBlendMode: 'overlay'
        }}
      >
        <Container>
          <Row className="text-center text-white custom-orders-content">
            <Col lg={8} className="mx-auto">
              <h2 className="display-5 mb-4">Custom Orders & Bulk Pricing</h2>
              <p className="lead mb-4">
                Need custom specifications or bulk quantities? Contact our team for personalized solutions
                and competitive pricing on large orders.
              </p>
              <div className="mb-4">
                <p className="h5 mb-2">+91 8920065965</p>
                <p className="h5 mb-2">sales@skytion.in</p>
                <p>Bulk orders • Custom specifications • Export inquiries</p>
              </div>
              <Link to="/contact" className="btn btn-primary btn-lg me-3">
                Request Quote
              </Link>
              <Link to="/manufacturing" className="btn btn-outline-light btn-lg">
                View Manufacturing Process
              </Link>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Products;
